import {
  useRef,
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { ThemeContext } from "../../../../theme/ThemeContext";
import AuthContext from "../../../../auth/AuthContext";
import AlertService from "../../../../services/AlertService";
import CatalogueService from "../../../../services/CatalogueService";
import { Skeleton } from "primereact/skeleton";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);


const roundedBarsPlugin = {
  id: 'roundedBars',
  beforeDatasetsDraw(chart) {
    const { ctx} = chart;
    
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
    
      const gradient = typeof dataset.backgroundColor === 'function' 
        ? dataset.backgroundColor({ chart, datasetIndex, dataIndex: 0 })
        : dataset.backgroundColor;
      
      meta.data.forEach((bar, index) => {
        const dataValue = dataset.data[index];
      
        if (dataValue > 0) {
          const { x, y, width, base } = bar;
          const radius = 12; 
          
          ctx.save();
          ctx.fillStyle = gradient;
          
          // Draw rounded rectangle with curves at top and bottom
          ctx.beginPath();
          ctx.moveTo(x - width / 2 + radius, base);
          ctx.lineTo(x + width / 2 - radius, base);
          ctx.quadraticCurveTo(x + width / 2, base, x + width / 2, base - radius);
          ctx.lineTo(x + width / 2, y + radius);
          ctx.quadraticCurveTo(x + width / 2, y, x + width / 2 - radius, y);
          ctx.lineTo(x - width / 2 + radius, y);
          ctx.quadraticCurveTo(x - width / 2, y, x - width / 2, y + radius);
          ctx.lineTo(x - width / 2, base - radius);
          ctx.quadraticCurveTo(x - width / 2, base, x - width / 2 + radius, base);
          ctx.closePath();
          ctx.fill();
          
          ctx.restore();
        }
      });
    });
    
    return false; 
  }
};

const CHART_CONSTANTS = {
  GRADIENT_HEIGHT: 300,
  BORDER_RADIUS: 12,
  ANIMATION_DURATION: 400,
  TOOLTIP_ANIMATION_DURATION: 100,
  SKELETON_BASE_HEIGHT: 60,
  SKELETON_HEIGHT_INCREMENT: 15,
};

const shortenMonthName = (monthName) => {
  return monthName?.substring(0, 3) || "UNK";
};

const getGradient = (ctx, chartArea, isDark) => {
  const gradient = ctx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom
  );
  gradient.addColorStop(0, "#5A57FF");
  gradient.addColorStop(
    1,
   "#b1aef8"
  );
  return gradient;
};

export default function CourseWiseChart() {
  const authContext = useContext(AuthContext);
  const chartRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLength, setDataLength] = useState(12);

const loadDashboard = useCallback(async (signal) => {
    setLoading(true);
    try {
      const data = await CatalogueService.GetIncidentLearnerDashboard(
        authContext,
        signal
      );

      if (signal?.aborted) return; 

      if (data.isSuccess && Array.isArray(data.data) && data.data.length > 0) {
        const counts = data.data.map((m) => m.count || 0);
        const labels = data.data.map((m) => shortenMonthName(m.monthName));
        const maxValue = Math.max(...counts, 1);
        const step = Math.pow(10, Math.floor(Math.log10(maxValue)) - 1);
        const roundedMax = Math.ceil(maxValue / step) * step;
        const stepSize = Math.ceil(roundedMax / 5 / step) * step;

        setChartData({
          labels,
          datasets: [
            {
              label: "Total Learners",
              data: counts,
              backgroundColor: (context) => {
                const chart = context.chart;
                const { ctx, chartArea } = chart;
                if (!chartArea) return isDark ? "#7B78FF" : "#5A57FF";
                return getGradient(ctx, chartArea, isDark);
              },
              borderRadius: 0,
              barPercentage: 0.7,
              categoryPercentage: 0.85,
            },
          ],
          yMax: roundedMax,
          yStep: stepSize,
        });
        setDataLength(data.data.length);
      } else {
        AlertService.error(data.errorMessage || "No data available");
        setChartData(null);
        setDataLength(12);
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      AlertService.error("Failed to load dashboard data");
      setChartData(null);
      setDataLength(12);
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, isDark]);

useEffect(() => {
    const controller = new AbortController(); 
    loadDashboard(controller.signal);
    return () => controller.abort(); 
  }, [loadDashboard]);

  const options = useMemo(
    () =>
      chartData
        ? {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
              duration: CHART_CONSTANTS.ANIMATION_DURATION,
              easing: "easeOutQuart",
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                backgroundColor: "#000000",
                cornerRadius: 8,
                padding: 12,
                titleFont: { family: "Poppins", size: 14, weight: "600" },
                bodyFont: { family: "Poppins", size: 13 },
                titleColor: "#ffffff",
                bodyColor: "#e0e0e0",
                borderColor: isDark ? "#555" : "#ccc",
                borderWidth: 1,
                animation: {
                  duration: CHART_CONSTANTS.TOOLTIP_ANIMATION_DURATION,
                  easing: "easeOutQuad",
                },
                callbacks: {
                  label: (context) =>
                    `${context.dataset.label}: ${context.raw}`,
                },
                filter: function(tooltipItem) {
                  return tooltipItem.raw > 0;
                },
              },
              roundedBars: true, 
            },
            scales: {
              x: {
                ticks: {
                  color: isDark ? "#e0e0e0" : "#555555",
                  font: { size: 12, weight: 600, family: "Poppins" },
                },
                grid: { display: false },
              },
              y: {
                beginAtZero: true,
                min: 0,
                max: chartData.yMax,
                ticks: {
                  stepSize: chartData.yStep,
                  color: isDark ? "#e0e0e0" : "#555555",
                  font: { size: 12, family: "Poppins" },
                },
                grid: {
                  color: isDark
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)",
                  borderDash: [4, 4],
                },
              },
            },
          }
        : {},
    [chartData, isDark]
  );

  return (
    <div
      className="charts d-flex flex-column"
      style={{
        padding: 16,
        minHeight: "337px",
        width: "100%",
        border: `1px solid ${isDark ? "#444" : "#eee"}`,
        borderRadius: "12px",
        backgroundColor: isDark ? "#222" : "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        {loading ? (
          <Skeleton width="150px" height="30px" />
        ) : (
          <h6
            style={{
              margin: 0,
              fontSize: 16,
              color: isDark ? "#e0e0e0" : "#333",
            }}
          >
            Learner Utilization
          </h6>
        )}
      </div>
      <div style={{ flexGrow: 1, position: "relative" }}>
        {loading || !chartData ? (
          <div className="h-100 d-flex align-items-end gap-2 px-2 py-2">
            {[...Array(dataLength)].map((_, i) => (
              <Skeleton
                key={i}
                width="100%"
                height={`${
                  CHART_CONSTANTS.SKELETON_BASE_HEIGHT +
                  i * CHART_CONSTANTS.SKELETON_HEIGHT_INCREMENT
                }px`}
                className="rounded-top"
              />
            ))}
          </div>
        ) : chartData.labels.length === 0 ? (
          <div>No data available</div>
        ) : (
          <Bar ref={chartRef} data={chartData} options={options} plugins={[roundedBarsPlugin]} />
        )}
      </div>
    </div>
  );
}